import { useEffect } from "react";

/**
 * Adds the CSS class `reveal--visible` to every element matching
 * `selector` when it enters the viewport.
 * Elements should start with the `reveal` class (opacity:0, translateY).
 *
 * Uses a MutationObserver to pick up elements added after mount
 * (e.g. async components, React Router route remounts on Back navigation).
 */
export function useScrollReveal(selector = ".reveal", threshold = 0.12) {
  useEffect(() => {
    const intersectionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("reveal--visible");
            intersectionObserver.unobserve(entry.target);
          }
        });
      },
      { threshold },
    );

    const observeEl = (el) => {
      // Skip elements already revealed
      if (!el.classList.contains("reveal--visible")) {
        intersectionObserver.observe(el);
      }
    };

    // Observe elements already in the DOM
    document.querySelectorAll(selector).forEach(observeEl);

    // Watch for new matching elements added to the DOM
    const mutationObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType !== Node.ELEMENT_NODE) return;
          if (node.matches && node.matches(selector)) {
            observeEl(node);
          }
          if (node.querySelectorAll) {
            node.querySelectorAll(selector).forEach(observeEl);
          }
        });
      });
    });

    mutationObserver.observe(document.body, { childList: true, subtree: true });

    return () => {
      intersectionObserver.disconnect();
      mutationObserver.disconnect();
    };
  }, [selector, threshold]);
}
